import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtAZN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { sfx } from "@/lib/sound";

const r2 = (v) => Math.round(v * 100) / 100;

export function usePlay(mode) {
  const { updateUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const field = mode === "real" ? "balance" : "demo_balance";
  const pending = useRef(null);
  useEffect(() => () => {
    if (pending.current) updateUser({ balance: pending.current.balance, demo_balance: pending.current.demo_balance });
  }, [updateUser]);

  const play = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(path, { ...body, mode });
      pending.current = data;
      updateUser({ [field]: r2(data[field] - (data.win || 0)) });
      return data;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
      return null;
    }
  };

  const finish = (data, { quiet } = {}) => {
    pending.current = null;
    updateUser({ balance: data.balance, demo_balance: data.demo_balance });
    setBusy(false);
    if (quiet) return;
    if (data.win > data.bet) { sfx.win(); toast.success(`Qazanc: +${fmtAZN(data.win)}`); }
    else if (!data.win) sfx.lose();
  };

  return { busy, setBusy, play, finish };
}

export const Panel = ({ children, className = "" }) => (
  <div className={`rounded-3xl border border-white/10 bg-[#111]/90 backdrop-blur-xl p-5 sm:p-6 space-y-5 h-fit ${className}`}>{children}</div>
);

export const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl bg-black/50 border border-white/5 p-3 text-center">
    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
    <p className="font-display font-bold text-[#FFD700]" data-testid={testid}>{value}</p>
  </div>
);

export function useSession(base, onResult) {
  const { updateUser } = useAuth();
  const [g, setG] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`${base}/active`).then(({ data }) => data && setG(data)).catch(() => {}); }, [base]);
  const call = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/${path}`, body);
      setG(data);
      updateUser({ balance: data.balance, demo_balance: data.demo_balance });
      if (data.status === "lost") sfx.lose();
      else if (data.status === "won") { sfx.cashout(); toast.success(`Qazanc: +${fmtAZN(data.win)}`); }
      onResult?.(data);
      return data;
    } catch (e) { toast.error(errMsg(e)); return null; } finally { setBusy(false); }
  };
  return { g, setG, busy, call, active: g?.status === "active" };
}

export const ModeNote = ({ g, mode }) => g?.status === "active" && g.mode !== mode ? (
  <p className="text-xs rounded-lg bg-amber-500/10 text-amber-300 p-2" data-testid="session-mode-note">Bu oyun {g.mode === "demo" ? "Demo" : "Real"} rejimində başlanıb</p>
) : null;

export const Label = ({ children }) => <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{children}</p>;

export const Felt = ({ children, className = "", testid }) => (
  <div className={`rounded-3xl p-5 sm:p-8 order-1 lg:order-2 min-h-[420px] border-4 border-[#5b3a1a] shadow-[inset_0_0_80px_rgba(0,0,0,0.6)] ${className}`}
    style={{ background: "radial-gradient(ellipse at center, #166534 0%, #0b3b20 70%, #052e16 100%)" }} data-testid={testid}>{children}</div>
);
